import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '@/hooks';
import { fetchBanners } from '@/services/api/bannerService';
import Modal from '@/components/common/Modal/Modal';
import styles from './SkylaBannerSlider.module.css';

/**
 * SkylaBannerSlider Component
 * Banner slider for logged-in users
 * Fetches banners from API using React Query
 * Shows popover/modal on mobile when banner is clicked
 */
const SkylaBannerSlider = () => {
  const { isMobile } = useScreenSize();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch banners using React Query
  const { data: banners = [], isLoading } = useQuery({
    queryKey: ['banners'],
    queryFn: fetchBanners,
    staleTime: 300000, // 5 minutes cache
    retry: 2,
  });

  // Calculate total slides based on mobile or desktop view
  // Desktop: show 3 banners at a time, so total slides = banners.length - 2 (minimum 1)
  // Mobile: show 1 banner at a time, so total slides = banners.length
  const totalSlides = isLoading
    ? 1
    : isMobile
    ? banners.length
    : Math.max(1, banners.length - 2);

  // Auto-scroll effect
  useEffect(() => {
    if (isLoading || banners.length === 0) return;

    const timer = setInterval(() => {
      setActiveIndex((current) => (current + 1) % totalSlides);
    }, 5000);

    return () => clearInterval(timer);
  }, [isLoading, totalSlides, banners.length]);

  // Handle dot click navigation
  const handleDotClick = (index) => {
    setActiveIndex(index);
  };

  // Handle banner click
  const handleBannerClick = (banner) => {
    if (isMobile) {
      // On mobile, show modal/popover
      setSelectedBanner(banner);
      setIsModalOpen(true);
    } else {
      // On desktop, you can add navigation or other action
      // For now, just show modal as well
      setSelectedBanner(banner);
      setIsModalOpen(true);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBanner(null);
  };

  // Handle banner action (navigate based on banner type)
  const handleBannerAction = () => {
    if (!selectedBanner) return;

    // Map banner types to routes
    const typeRouteMap = {
      casino: '/casino',
      sports: '/sports',
      slots: '/slots',
      live: '/live-casino',
      bonus: '/bonus',
    };

    const route = typeRouteMap[selectedBanner.type?.toLowerCase()] || '/';
    navigate(route);
    handleCloseModal();
  };

  // Render loading shimmer
  if (isLoading) {
    return (
      <div className={styles.carouselContainer}>
        <div className={styles.shimmerWrapper}>
          {[...Array(isMobile ? 1 : 3)].map((_, index) => (
            <div key={index} className={styles.shimmerSlide}>
              <div className={styles.shimmerContent}></div>
            </div>
          ))}
        </div>
        <div className={styles.paginationDots}>
          <button className={`${styles.dot} ${styles.active}`}></button>
        </div>
      </div>
    );
  }

  // Return empty if no banners
  if (banners.length === 0) {
    return null;
  }

  return (
    <>
      <div className={styles.carouselContainer}>
        <div
          className={styles.slideTrack}
          style={{
            transform: isMobile
              ? `translateX(-${activeIndex * 100}%)`
              : `translateX(calc(-${activeIndex} * (33.33% + 10px)))`,
          }}
        >
          {banners.map((banner) => (
            <div key={banner.id} className={styles.slide}>
              <div 
                className={styles.slideContent}
                onClick={() => handleBannerClick(banner)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleBannerClick(banner);
                  }
                }}
                aria-label={`View ${banner.type || 'banner'} details`}
              >
                <img
                  src={banner.image}
                  alt={banner.type || 'Banner Image'}
                  className={styles.slideImage}
                  loading="lazy"
                />
              </div>
            </div>
          ))}
        </div>

        <div className={styles.paginationDots}>
          {[...Array(totalSlides)].map((_, index) => (
            <button
              key={index}
              className={`${styles.dot} ${index === activeIndex ? styles.active : ''}`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Mobile/Desktop Banner Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        size={isMobile ? 'lg' : 'md'}
        className={styles.bannerModal}
      >
        {selectedBanner && (
          <div className={styles.modalContent}>
            <div className={styles.modalImageContainer}>
              <img
                src={selectedBanner.image}
                alt={selectedBanner.type || 'Banner Image'}
                className={styles.modalImage}
              />
            </div>
            {selectedBanner.type && (
              <div className={styles.modalActions}>
                <button
                  className={styles.actionButton}
                  onClick={handleBannerAction}
                >
                  Explore {selectedBanner.type}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default SkylaBannerSlider;
